import { QuestionnaireResponseItem } from '@medplum/fhirtypes'
import { AdapterOptions } from '../../../../../adapters/types'
import { DischargeFormData } from '../../../../types/formData'
import { DischargeFormField } from '../../../../types/formField'
import { DischargeAssessmentQuestionnaireItem } from '../../../../types/questionnaire'
import { getAnswerValuesFromItems } from '../../../../../helpers'
import { BaseSubsectionAdapter } from '../../../../../adapters/BaseSubsectionAdapter'
import { FlatAnswerMutation } from '../../../../../adapters/types'

/**
 * Vitals subsection adapter
 * Handles all vital signs measurements (8 fields)
 */
export class VitalsAdapter extends BaseSubsectionAdapter<DischargeFormData> {
  extractFormData(
    flatAnswers: Record<string, QuestionnaireResponseItem>,
    _options?: AdapterOptions
  ): Partial<DischargeFormData> {
    const flatAnswersArray = Object.values(flatAnswers)

    // Direct field extractions for all vital signs
    const temperature = getAnswerValuesFromItems(
      flatAnswersArray,
      DischargeAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_VITALS_TEMPERATURE
    )?.at(0)

    const pulse = getAnswerValuesFromItems(
      flatAnswersArray,
      DischargeAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_VITALS_PULSE
    )?.at(0)

    const respirations = getAnswerValuesFromItems(
      flatAnswersArray,
      DischargeAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_VITALS_RESPIRATIONS
    )?.at(0)

    const o2Saturation = getAnswerValuesFromItems(
      flatAnswersArray,
      DischargeAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_VITALS_O2_SATURATION
    )?.at(0)

    const bloodPressure = getAnswerValuesFromItems(
      flatAnswersArray,
      DischargeAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_VITALS_BLOOD_PRESSURE
    )?.at(0)

    const height = getAnswerValuesFromItems(
      flatAnswersArray,
      DischargeAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_VITALS_HEIGHT
    )?.at(0)

    const bloodSugar = getAnswerValuesFromItems(
      flatAnswersArray,
      DischargeAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_VITALS_BLOOD_SUGAR
    )?.at(0)

    const weight = getAnswerValuesFromItems(
      flatAnswersArray,
      DischargeAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_VITALS_WEIGHT
    )?.at(0)

    return {
      [DischargeFormField.VITALS_TEMPERATURE]: temperature,
      [DischargeFormField.VITALS_PULSE]: pulse,
      [DischargeFormField.VITALS_RESPIRATIONS]: respirations,
      [DischargeFormField.VITALS_O2_SATURATION]: o2Saturation,
      [DischargeFormField.VITALS_BLOOD_PRESSURE]: bloodPressure,
      [DischargeFormField.VITALS_HEIGHT]: height,
      [DischargeFormField.VITALS_BLOOD_SUGAR]: bloodSugar,
      [DischargeFormField.VITALS_WEIGHT]: weight
    }
  }

  buildFlatAnswers(): FlatAnswerMutation[] {
    const mutations: FlatAnswerMutation[] = []

    if (this.formDataFieldExists(DischargeFormField.VITALS_TEMPERATURE)) {
      mutations.push({
        linkId: DischargeAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_VITALS_TEMPERATURE,
        value: this.getFormDataField(DischargeFormField.VITALS_TEMPERATURE)
      })
    }
    if (this.formDataFieldExists(DischargeFormField.VITALS_PULSE)) {
      mutations.push({
        linkId: DischargeAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_VITALS_PULSE,
        value: this.getFormDataField(DischargeFormField.VITALS_PULSE)
      })
    }
    if (this.formDataFieldExists(DischargeFormField.VITALS_RESPIRATIONS)) {
      mutations.push({
        linkId: DischargeAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_VITALS_RESPIRATIONS,
        value: this.getFormDataField(DischargeFormField.VITALS_RESPIRATIONS)
      })
    }
    if (this.formDataFieldExists(DischargeFormField.VITALS_O2_SATURATION)) {
      mutations.push({
        linkId: DischargeAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_VITALS_O2_SATURATION,
        value: this.getFormDataField(DischargeFormField.VITALS_O2_SATURATION)
      })
    }
    if (this.formDataFieldExists(DischargeFormField.VITALS_BLOOD_PRESSURE)) {
      mutations.push({
        linkId: DischargeAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_VITALS_BLOOD_PRESSURE,
        value: this.getFormDataField(DischargeFormField.VITALS_BLOOD_PRESSURE)
      })
    }
    if (this.formDataFieldExists(DischargeFormField.VITALS_HEIGHT)) {
      mutations.push({
        linkId: DischargeAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_VITALS_HEIGHT,
        value: this.getFormDataField(DischargeFormField.VITALS_HEIGHT)
      })
    }
    if (this.formDataFieldExists(DischargeFormField.VITALS_BLOOD_SUGAR)) {
      mutations.push({
        linkId: DischargeAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_VITALS_BLOOD_SUGAR,
        value: this.getFormDataField(DischargeFormField.VITALS_BLOOD_SUGAR)
      })
    }
    if (this.formDataFieldExists(DischargeFormField.VITALS_WEIGHT)) {
      mutations.push({
        linkId: DischargeAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_VITALS_WEIGHT,
        value: this.getFormDataField(DischargeFormField.VITALS_WEIGHT)
      })
    }

    return mutations
  }
}
